import * as yup from 'yup';
import { useTranslation } from 'react-i18next';
import { Button, Grid, MenuItem, Slider, TextField, ThemeProvider, useTheme } from '@mui/material';
import React from 'react';
import { FieldArray, Form, Formik, FormikHelpers } from 'formik';
import { isEqual, omit } from 'lodash';
import { usePostHog } from 'posthog-js/react';
import { KeyedMutator } from 'swr/_internal';
import Typography from '@mui/material/Typography';
import { CacheProvider } from '@emotion/react';
import { Add } from '@mui/icons-material';
import {
  AccountData,
  AccountService,
  AddonStatus,
  AddonUIObjectData,
  EnumPriceData,
  LabelData,
  NumberPriceData,
  OrderPageData,
  PartialLabelData,
  VoucherSoldTypes,
} from '../../../api';
import { OrderPageUpdate } from '../../OrderPageSettingsPage';
import { UseAuthPermissions } from '../../../session/UseAuthPermissions';
import { FormTextField } from '../../formComponents/FormTextField';
import { SettingsRow } from '../../formComponents/SettingsRow';
import { cacheLtr, useLanguage } from '../../../common/GeneralUtils';
import { FormLabelField } from '../../formComponents/FormLabelField';
import { FormRichTextField } from '../../formComponents/FormRichTextField';
import TTLLocalStorage from '../../../utils/TTLLocalStorage';
import { useAuth } from '../../../session/InternalAuthProvider';
import { AddonEditor } from './AddonEditor';
import { findMultipleTicketsUiObject } from '../../../common/multipleTicketsUtils';
import { SectionSaveRegistrar } from '../../saveHeader/SectionSaveRegistrar';
import type { ChangeEntry } from '../../saveHeader/SaveHeaderContext';
import { htmlValuesDiffer, renderRichDiffPair } from '../../saveHeader/richTextDiff';
import { SettingsPageTabs } from '../../SettingsPageTabs';

type SettingsSectionProps = {
  orderPage: OrderPageData;
  orderPageInitialValues: OrderPageUpdate;
  updateLocalAccount: KeyedMutator<AccountData>;
};

export type SliderFormValues = {
  id: string;
  type: 'slider';
  min_value: number;
  max_value: number;
  label: PartialLabelData;
};

export type AddonOptionFormValue = {
  id: string;
  label: PartialLabelData;
  base_price: number;
  price_per_additional_value: number;
  icon: string;
};

export type AddonFormValue = {
  id: string;
  type: 'addon';
  label: PartialLabelData;
  options: AddonOptionFormValue[];
};

export const generateObjectId = () =>
  Array.from(crypto.getRandomValues(new Uint8Array(12)))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');

export type VouchersConfigFormValues = {
  sold_types: VoucherSoldTypes | '';
  max_valid_days?: number;
  link_to_workshop_details: string;
  voucher_widget_title: PartialLabelData;
  voucher_widget_subtitle: PartialLabelData;
};

export type SettingsFormValues = {
  slider: SliderFormValues;
  vouchers_config: VouchersConfigFormValues;
  disclaimers?: PartialLabelData;
  addons: AddonFormValue[];
};

const initAddonsFromOrderPage = (orderPage: OrderPageData): AddonFormValue[] => {
  const addonObjects = orderPage.objects.filter(
    (x) => x.type === 'addon' && (x as AddonUIObjectData).status !== AddonStatus.ARCHIVED,
  ) as AddonUIObjectData[];
  return addonObjects.map((addon) => {
    const enumPrice = orderPage.price.find((p) => p.type === 'enum' && (p as EnumPriceData).name === addon.id) as
      | EnumPriceData
      | undefined;
    return {
      id: addon.id,
      type: 'addon' as const,
      label: addon.label,
      options: addon.options.map((opt) => {
        const priceEntry = enumPrice?.options?.find((o) => o.value === opt.id);
        const numPrice = priceEntry?.price as NumberPriceData | undefined;
        return {
          id: opt.id,
          label: opt.label,
          base_price: numPrice?.base_price || 0,
          price_per_additional_value: numPrice?.price_per_additional_value || 0,
          icon: opt.icon || '',
        };
      }),
    };
  });
};

const buildLabel = (partial: PartialLabelData, languages: string[]) =>
  Object.fromEntries(languages.map((lang) => [lang, partial[lang as keyof PartialLabelData] || ''])) as LabelData;

const compactLabel = (label: PartialLabelData): PartialLabelData | undefined => {
  const filledEntries = Object.entries(label).filter(([, text]) => text);
  return filledEntries.length ? Object.fromEntries(filledEntries) : undefined;
};

const buildAddonObjects = (addons: AddonFormValue[], languages: string[]): AddonUIObjectData[] =>
  addons.map((a) => ({
    id: a.id,
    type: 'addon',
    label: buildLabel(a.label, languages),
    options: a.options.map((o) => ({ id: o.id, label: buildLabel(o.label, languages), icon: o.icon || undefined })),
  }));

const buildAddonPrices = (addons: AddonFormValue[]): EnumPriceData[] =>
  addons.map((a) => ({
    type: 'enum',
    data_type: 'custom' as any,
    name: a.id,
    options: a.options.map((o) => ({
      value: o.id,
      price: {
        type: 'number',
        data_type: 'persons' as any,
        base_price: o.base_price,
        base_value: 1,
        price_per_additional_value: o.price_per_additional_value,
      } as NumberPriceData,
    })),
  }));

export const WidgetSection = ({ orderPage, orderPageInitialValues, updateLocalAccount }: SettingsSectionProps) => {
  const { t } = useTranslation();
  const { readOnly } = UseAuthPermissions();
  const { authState } = useAuth();
  const account = authState.account as unknown as AccountData;
  const languages = account.customer_facing_widgets_languages;
  const language = useLanguage();
  const posthog = usePostHog();
  const theme = useTheme();

  const hasMultipleTickets = !!findMultipleTicketsUiObject(orderPage);
  const validationSchema = yup.object().shape({
    slider: hasMultipleTickets
      ? yup.object().notRequired()
      : yup.object().shape({
          min_value: yup
            .number()
            .required(t('editOrderPage.yup.required'))
            .min(0, t('editOrderPage.yup.hasToBePoitive')),
          max_value: yup
            .number()
            .required(t('editOrderPage.yup.required'))
            .min(0, t('editOrderPage.yup.hasToBePoitive'))
            .min(yup.ref('min_value'), t('editOrderPage.yup.hasToBeMoreThanMin')),
        }),
  });

  const slider = orderPage.objects.find((x) => x.type === 'slider');
  const initialValues: SettingsFormValues = {
    slider: {
      // @ts-ignore
      min_value: slider?.min_value,
      // @ts-ignore
      max_value: slider?.max_value,
      // @ts-ignore
      label: slider?.label,
      id: slider?.id || '',
      type: 'slider',
    },
    vouchers_config: {
      sold_types: orderPage.vouchers_config?.sold_types || '',
      max_valid_days: orderPage.vouchers_config?.max_valid_days,
      link_to_workshop_details: orderPage.vouchers_config?.link_to_workshop_details || '',
      voucher_widget_title: { en: '', he: '', ...orderPage.vouchers_config?.voucher_widget_title },
      voucher_widget_subtitle: { en: '', he: '', ...orderPage.vouchers_config?.voucher_widget_subtitle },
    },
    disclaimers: orderPage.disclaimers,
    addons: initAddonsFromOrderPage(orderPage),
  };

  const existingAddonIds = new Set(
    (orderPage.objects.filter((x) => x.type === 'addon') as AddonUIObjectData[]).map((a) => a.id),
  );

  const handleSubmit = async (values: SettingsFormValues, { setStatus }: FormikHelpers<SettingsFormValues>) => {
    try {
      posthog.capture('Editing settings', { section: 'widgetSection', settings: values });
      const addonObjects = buildAddonObjects(values.addons, languages);
      const addonPrices = buildAddonPrices(values.addons);
      const nonAddonPrices = orderPageInitialValues.price.filter(
        (p) => !(p.type === 'enum' && existingAddonIds.has((p as EnumPriceData).name || '')),
      );
      const { slider: sliderValues, vouchers_config: vouchersValues, ...restValues } = values;
      const sliderPayload =
        !hasMultipleTickets && sliderValues.min_value != null && sliderValues.max_value != null
          ? { slider: sliderValues }
          : {};
      const updatedAccount = await AccountService.updateOrderPage(
        omit(
          {
            ...orderPageInitialValues,
            ...restValues,
            ...sliderPayload,
            vouchers_config: {
              sold_types: vouchersValues.sold_types || undefined,
              max_valid_days: vouchersValues.max_valid_days,
              link_to_workshop_details: vouchersValues.link_to_workshop_details || undefined,
              voucher_widget_title: compactLabel(vouchersValues.voucher_widget_title),
              voucher_widget_subtitle: compactLabel(vouchersValues.voucher_widget_subtitle),
            },
            addons: addonObjects,
            price: [...nonAddonPrices, ...addonPrices],
            id: orderPage.id,
            duration: (orderPageInitialValues.duration || 0) * 60,
          },
          'availability_settings.restrictions',
          'objects',
        ),
      );
      await updateLocalAccount(updatedAccount);
      TTLLocalStorage.removeByPrefix('order-page-state');
    } catch (e) {
      setStatus({ error: (e as Error)?.message || t('saveHeader.errorGeneric') });
      throw e;
    }
  };

  const getChanges = (values: SettingsFormValues, iv: SettingsFormValues): ChangeEntry[] => {
    const changes: ChangeEntry[] = [];
    if (!isEqual(values.slider, iv.slider)) {
      changes.push({
        label: t('editOrderPage.slider.sliderDescription'),
        from: `${iv.slider?.min_value ?? '—'} – ${iv.slider?.max_value ?? '—'}`,
        to: `${values.slider?.min_value ?? '—'} – ${values.slider?.max_value ?? '—'}`,
      });
    }
    languages.forEach((lang) => {
      if (htmlValuesDiffer(values.disclaimers?.[lang], iv.disclaimers?.[lang])) {
        changes.push({
          label: `${t('editOrderPage.disclaimers')} (${t(`emailTemplates.${lang}`)})`,
          ...renderRichDiffPair(iv.disclaimers?.[lang], values.disclaimers?.[lang]),
        });
      }
    });
    if (!isEqual(values.vouchers_config, iv.vouchers_config)) {
      changes.push({
        label: t('editOrderPage.vouchers.title'),
        from: iv.vouchers_config.sold_types || t('editOrderPage.vouchers.inheritShort'),
        to: values.vouchers_config.sold_types || t('editOrderPage.vouchers.inheritShort'),
      });
    }
    if (!isEqual(values.addons, iv.addons)) {
      changes.push({
        label: t('editOrderPage.addons.title'),
        from: `${iv.addons?.length || 0}`,
        to: `${values.addons?.length || 0}`,
      });
    }
    return changes;
  };

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}>
      {(formik) => {
        const { isSubmitting, values } = formik;
        const formIsDisabled = readOnly || isSubmitting;
        return (
          <Form onSubmit={formik.handleSubmit}>
            <SectionSaveRegistrar tabKey={SettingsPageTabs.WIDGET} getChanges={getChanges} disabled={readOnly} />
            <Grid container flexDirection="column">
              {!hasMultipleTickets && (
                <SettingsRow label={t('editOrderPage.slider.sliderDescription')}>
                  <CacheProvider value={cacheLtr}>
                    <ThemeProvider theme={{ ...theme, direction: 'ltr' }}>
                      <Grid container flexDirection="column" gap={2}>
                        <FormLabelField labelFieldName="slider.label" label={''} disabled={formIsDisabled} />
                        <Typography variant="body2" color="text.secondary">
                          {t('editOrderPage.slider.sliderCaption')}
                        </Typography>
                        <Grid container flexDirection="column">
                          <Slider value={2} min={1} max={7} disabled />
                          <Grid
                            container
                            flexWrap="nowrap"
                            flexDirection={language === 'he' ? 'row-reverse' : 'row'}
                            justifyContent="space-between">
                            <Grid item alignContent="flex-start">
                              <Typography variant="caption">{t('editOrderPage.slider.minValue')}</Typography>
                              <FormTextField
                                isFieldUpdate
                                width={70}
                                fieldName="slider.min_value"
                                disabled={formIsDisabled}
                                type="number"
                              />
                            </Grid>
                            <Grid item alignContent="flex-end">
                              <Typography variant="caption">{t('editOrderPage.slider.maxValue')}</Typography>
                              <FormTextField
                                isFieldUpdate
                                width={70}
                                fieldName="slider.max_value"
                                disabled={formIsDisabled}
                                type="number"
                              />
                            </Grid>
                          </Grid>
                        </Grid>
                      </Grid>
                    </ThemeProvider>
                  </CacheProvider>
                </SettingsRow>
              )}

              <SettingsRow label={t('editOrderPage.disclaimers')}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {t('editOrderPage.disclaimersCaption')}
                </Typography>
                <FormRichTextField labelFieldName="disclaimers" disabled={formIsDisabled} />
              </SettingsRow>

              <SettingsRow label={t('editOrderPage.vouchers.title')}>
                <Grid container flexDirection="column" gap={2}>
                  <Typography variant="body2" color="text.secondary">
                    {t('editOrderPage.vouchers.subtitle')}
                  </Typography>
                  <Grid item md={4} xs={12}>
                    <TextField
                      select
                      fullWidth
                      name="vouchers_config.sold_types"
                      label={t('accountSettings.soldTypes.label')}
                      value={values.vouchers_config.sold_types}
                      onChange={formik.handleChange}
                      disabled={formIsDisabled}>
                      <MenuItem value="">
                        {t('editOrderPage.vouchers.inherit', {
                          value: t(`accountSettings.soldTypes.${account.vouchers_config?.sold_types}`),
                        })}
                      </MenuItem>
                      {Object.values(VoucherSoldTypes).map((soldType) => (
                        <MenuItem key={soldType} value={soldType}>
                          {t(`accountSettings.soldTypes.${soldType}`)}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <FormTextField
                    isFieldUpdate
                    fieldName="vouchers_config.max_valid_days"
                    label={t('accountSettings.vouchersMaxValidDays')}
                    caption={t('editOrderPage.vouchers.maxValidDaysCaption', {
                      value: account.vouchers_config?.max_valid_days,
                    })}
                    type="number"
                    min={1}
                    disabled={formIsDisabled}
                  />
                </Grid>
              </SettingsRow>

              <SettingsRow label={t('editOrderPage.addons.title')} isLast>
                <Grid container flexDirection="column" gap={2}>
                  <Typography variant="body2" color="text.secondary">
                    {t('editOrderPage.addons.subtitle')}
                  </Typography>
                  <FieldArray name="addons">
                    {({ push, remove }) => (
                      <Grid container flexDirection="column" gap={2}>
                        {values.addons.length === 0 && (
                          <Typography variant="body2" color="text.secondary">
                            {t('editOrderPage.addons.noAddons')}
                          </Typography>
                        )}
                        {values.addons.map((addon, idx) => (
                          <AddonEditor
                            key={addon.id}
                            index={idx}
                            disabled={formIsDisabled}
                            onDelete={() => remove(idx)}
                          />
                        ))}
                        {values.addons.length < 2 ? (
                          <Button
                            startIcon={<Add />}
                            onClick={() =>
                              push({
                                id: generateObjectId(),
                                type: 'addon',
                                label: {},
                                options: [],
                              } as AddonFormValue)
                            }
                            disabled={formIsDisabled}
                            variant="outlined"
                            color="inherit"
                            sx={{ alignSelf: 'flex-start' }}>
                            {t('editOrderPage.addons.addAddon')}
                          </Button>
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            {t('editOrderPage.addons.maxAddonsReached')}
                          </Typography>
                        )}
                      </Grid>
                    )}
                  </FieldArray>
                </Grid>
              </SettingsRow>
            </Grid>
          </Form>
        );
      }}
    </Formik>
  );
};
